import { useLayoutEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getFocusableElements = container => Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));

const focusFirstElement = container => {
    const focusableElements = getFocusableElements(container);
    (focusableElements[0] || container).focus();
};

const trapTabKey = (event, container) => {
    const focusableElements = getFocusableElements(container);
    if (!focusableElements.length) {
        event.preventDefault();
        container.focus();
        return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const { activeElement } = document;

    if (event.shiftKey && (activeElement === firstElement || activeElement === container)) {
        event.preventDefault();
        lastElement.focus();
    } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
    }
};

/**
 * Moves focus into `containerRef` while `isActive`, keeps Tab inside it, and calls `onEscape` on Escape.
 */
const useFocusTrap = (containerRef, isActive, onEscape) => {
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;

    useLayoutEffect(() => {
        if (!isActive) {
            return undefined;
        }

        const container = containerRef.current;
        if (!container) {
            return undefined;
        }

        if (!container.contains(document.activeElement)) {
            container.focus({ preventScroll: true });
        }

        const handleKeyDown = event => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onEscapeRef.current?.();
                return;
            }

            if (event.key === 'Tab') {
                trapTabKey(event, container);
            }
        };

        const handleFocusIn = event => {
            if (!container.contains(event.target)) {
                focusFirstElement(container);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('focusin', handleFocusIn);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('focusin', handleFocusIn);
        };
    }, [containerRef, isActive]);
};

export default useFocusTrap;
